'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/navigation/button/styles.css';
import { Menu, MenuDivider, MenuItem } from 'BomdisoftComponents/menu/index';
import { toNavigationItemTestId } from 'BomdisoftComponents/navigation/button/render';
import { useIsActiveMenu } from 'BomdisoftComponents/navigation/hooks';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { NavigationMenuItem, PageRoute } from 'BomdisoftComponents/types';
import { getPathname, useCurrentPage } from '@bomdisoft/utilities';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { MouseEvent, useRef, useState } from 'react';

export type NavigationButtonProps = {
  route: NavigationMenuItem;
  onlyShowIconsWhenActive?: boolean;
  showOnlyIconsOnSmallScreens?: boolean;
};

export const NavigationButton = ({ route, showOnlyIconsOnSmallScreens = true, onlyShowIconsWhenActive = false }: NavigationButtonProps) => {
  const currentPage = useCurrentPage();
  const router = useRouter();
  const isActiveMenu = useIsActiveMenu(route);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLLIElement>(null);

  const showList = () => (route.pages && route.pages.length > 0 ? setOpen(true) : null);
  const handleClose = () => setOpen(false);
  const goto = (link: string) => () => {
    router.push(link);
    handleClose();
  };

  const hasLabel = !route.iconOnly && Boolean(route.name);
  const iconGivesWayToLabel = onlyShowIconsWhenActive && !route.isActive && hasLabel;

  const handleActivate = (event: MouseEvent<HTMLElement>) => {
    if (route.action) route.action(event);
    else showList();
  };

  const listItem = (
    <li
      className='bomdisoft-navigation-button'
      data-active={isActiveMenu || route.isActive ? '' : undefined}
      data-flexible={showOnlyIconsOnSmallScreens && !route.iconOnly ? '' : undefined}
      data-icon-gives-way={iconGivesWayToLabel ? '' : undefined}
      data-icon-only={route.iconOnly ? '' : undefined}
      ref={anchorRef}
    >
      <button aria-label={route.name} className='bomdisoft-navigation-button-trigger' onClick={handleActivate} type='button'>
        <span className='bomdisoft-navigation-button-route'>
          <span aria-hidden='true' className='bomdisoft-navigation-button-icon'>
            {route.icon}
          </span>
          {hasLabel ? (
            <span className='bomdisoft-navigation-button-name' data-with-icon={route.icon ? '' : undefined}>
              {route.name}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  );

  const button = route.iconOnly && !route.pages ? <Tooltip title={route.name}>{listItem}</Tooltip> : listItem;

  const content = route.link ? (
    <Link className='bomdisoft-navigation-button-link' data-testid={route.testId} href={route.link.url} key={route.link.url}>
      {button}
    </Link>
  ) : (
    button
  );

  const getMenuItem = (page: PageRoute) => {
    const testID = toNavigationItemTestId(route.name, page.name);
    const icon = page.icon ? (
      <span aria-hidden='true' className='bomdisoft-navigation-button-menu-icon'>
        {page.icon}
      </span>
    ) : null;

    return page.link ? (
      <MenuItem key={page.link.url} onClick={goto(page.link.url)} selected={getPathname(page.link) === currentPage}>
        {icon}
        {page.name}
      </MenuItem>
    ) : (
      <MenuItem key={page.name} onClick={page.action} testId={testID}>
        {icon}
        {page.name}
      </MenuItem>
    );
  };

  return route.pages ? (
    <>
      {content}
      <Menu anchorRef={anchorRef} onClose={handleClose} open={open} testId={`menu-dialog-for-${route.testId}`}>
        {route.pages?.map((page, index) => (page.type == 'Divider' ? <MenuDivider key={index} /> : getMenuItem(page)))}
      </Menu>
    </>
  ) : (
    content
  );
};
